/**
 * @file Shared modal opener for non-parent domain forms.
 */

import {
    findItem,
} from "../utils/itemLookup.js";
import {
    formatCurrency,
} from "../utils/displayUtils.js";
import { handleFormSubmit } from './modalSubmitRouter.js';
import { formItemData, setFormItemData } from './formModalState.js';
import {
    applyAddFormContext,
    clearSpecialisedFormShellArtifacts,
    cloneFormItemData,
    getFormHeaderActionsHtml,
    getFormModalTitle,
} from './formShell.js';
import {
    configureDefaultFormActions,
    activateFormModalAccessibility,
    configureInviteFormActions,
    formModalElement,
    lockFormModalScroll,
} from './modalShell.js';
import { setupMultiSelectFields } from './multiSelectFields.js';
import { updateSelectStyles } from './selectStyles.js';

/**
 * Opens a standard form modal for domain-owned non-parent forms.
 *
 * @param {string} type Form type stored on the modal dataset.
 * @param {object | null} item Existing item or initial data.
 * @param {object} options Form owner callbacks.
 * @param {Function} options.getFieldsHtml HTML renderer for the form body.
 * @param {Function} [options.afterRender] Callback after common listeners bind.
 * @param {boolean} [options.useInviteActions] Whether to enable invite buttons.
 * @returns {void}
 */
export function openStandardForm(type, item = null, options = {}) {
    if (!formModalElement) { console.error("Form modal element (#form-modal) not found."); return; }
    const formTitle = formModalElement.querySelector('#form-title');
    const formHeaderActions = formModalElement.querySelector('#form-header-actions');
    const formFields = formModalElement.querySelector('#form-fields');
    const itemForm = formModalElement.querySelector('#item-form');
    if (!formTitle || !formHeaderActions || !formFields || !itemForm) { console.error("Required elements missing within #form-modal."); return; }

    const isEditing = !!item?.id;
    const title = getFormModalTitle({ type, item, isEditing, findItem });
    formTitle.textContent = title;
    formHeaderActions.innerHTML = getFormHeaderActionsHtml(type);
    clearSpecialisedFormShellArtifacts(formModalElement);
    formFields.innerHTML = '';

    setFormItemData(applyAddFormContext(
        cloneFormItemData(item),
        item,
        isEditing,
    ));

    try {
        formFields.innerHTML = options.getFieldsHtml(formItemData);
    } catch (error) {
        console.error(`Error generating form fields for type "${type}":`, error);
        formFields.innerHTML = `<p class="text-red-500">Error loading form fields.</p>`;
    }

    itemForm.dataset.type = type;
    itemForm.dataset.id = formItemData.id || '';
    itemForm.dataset.parentId = formItemData.parentId || '';
    itemForm.onsubmit = handleFormSubmit;
    configureDefaultFormActions();
    if (options.useInviteActions) {
        configureInviteFormActions();
    }

    formFields.querySelectorAll('.currency-input').forEach(input => {
        input.value = formatCurrency(formItemData[input.name]);
        input.oninput = (e) => {
            let value = e.target.value.replace(/[^0-9.]/g, '');
            if (value.split('.').length > 2) {
                value = value.substring(0, value.lastIndexOf('.'));
            }
            e.target.value = value;
            formItemData[e.target.name] = value;
        };
        input.onblur = (e) => {
            e.target.value = formatCurrency(formItemData[e.target.name]);
        };
        input.onfocus = (e) => {
            e.target.value = formItemData[e.target.name] ?? '';
        };
    });

    requestAnimationFrame(() => { setupMultiSelectFields(type, formItemData); });

    formFields.querySelectorAll('select').forEach(select => {
        updateSelectStyles(select, type);
        select.onchange = (e) => {
            updateSelectStyles(e.target, type);
        };
    });

    const updateDateColor = (input) => {
        if (!input.value) {
            input.classList.add('text-gray-500', 'dark:text-gray-400');
            input.classList.remove('text-gray-900', 'dark:text-gray-200');
        } else {
            input.classList.remove('text-gray-500', 'dark:text-gray-400');
            input.classList.add('text-gray-900', 'dark:text-gray-200');
        }
    };
    formFields.querySelectorAll('input[type="date"], input[type="datetime-local"]').forEach(input => {
        updateDateColor(input);
        input.onchange = (e) => updateDateColor(e.target);
        input.oninput = (e) => updateDateColor(e.target);
    });

    if (typeof options.afterRender === 'function') {
        options.afterRender({ formFields, formItemData, itemForm });
    }

    formModalElement.classList.add('flex');
    lockFormModalScroll();
    activateFormModalAccessibility(
        options.initialFocus?.({formFields, itemForm}) ||
        formFields.querySelector("input:not([disabled]), textarea:not([disabled])"),
    );
}
